import * as React from 'react';
import { DataShape, Field, FormProps, reduxForm } from 'redux-form';
import { css, StyleSheet } from 'aphrodite';
import { colors, fonts, media } from 'consumer-components';

import * as formUtils from 'login-utils/formUtils';
import ErrorMessage from 'login-components/Common/Notifications/ErrorMessage/ErrorMessage';
import LoanEstimatePrompt from 'login-components/LoanEstimate/LoanEstimatePrompt';
import PaneLayoutConstants from 'login-constants/paneLayoutConstants';
import PrimaryButton from 'login-components/Common/Buttons/PrimaryButton';
import { getWindow } from 'login-utils/globalWrappers';
import { messageContainsEmail } from 'login-utils/errorUtils';
import { isHELOC } from 'login-utils/loanUtils';

export const SIMPLE_FORM_ERROR_ID = 'SimpleForm-formError';

interface SimpleFormProps extends FormProps<any, any, any> {
  inputs: FormInput[];
  submitFn(values: DataShape): Promise<void>;
  actionText: string;
  disclaimerText?: string;
  showEstimatePrompt?: boolean;
  loanType?: string;
}

const styles = StyleSheet.create({
  container: {
    ...PaneLayoutConstants.splitContentStaticWidthMixin,
    ...fonts.Roboto,
    position: 'relative',
    top: -10,
  },
  inputWrapper: {
    paddingBottom: 20,
    paddingTop: 10,
  },
  errorWrapper: {
    paddingBottom: 20,
  },
  detailsWrapper: {
    color: colors.clayPlus3,
    fontSize: 11,
    paddingTop: 20,
  },
  buttonWrapper: {
    paddingTop: 20,
    [media.smallView]: {
      width: 190,
    },
    [media.mediumView]: {
      width: 190,
    },
    [media.mobileMedium]: {
      width: '100%',
    },
  },
});

export class SimpleForm extends React.Component<SimpleFormProps, {}> {
  render() {
    // handleSubmit always exists although typed optional
    const { disclaimerText, actionText, error, handleSubmit, inputs, submitFn, submitting, loanType } = this.props;
    const errorContainsPii = error && messageContainsEmail(error);
    const window = getWindow();
    const showEstimatePrompt = window.DEPLOYMENT_CONFIG.enableLoanEstimateForm && !isHELOC(loanType);

    return (
      <form className={`${css(styles.container)} it-SimpleForm`} noValidate onSubmit={handleSubmit!(submitFn)}>
        {inputs.map(input => (
          <div className={css(styles.inputWrapper)} key={input.name}>
            <Field {...input} hidePlaceholder={true} />
          </div>
        ))}
        {error && (
          <div className={css(styles.errorWrapper)}>
            <ErrorMessage
              errorText={error}
              errorId={SIMPLE_FORM_ERROR_ID}
              data-exclude-fullstory={errorContainsPii && 'always'}
            />
          </div>
        )}
        {disclaimerText && (
          <div className={css(styles.detailsWrapper)} data-it="disclaimer-text">
            {disclaimerText}
          </div>
        )}
        {showEstimatePrompt && (
          <div className={css(styles.detailsWrapper)}>
            <LoanEstimatePrompt />
          </div>
        )}
        <div className={css(styles.buttonWrapper)}>
          <PrimaryButton text={actionText} disabled={submitting} fontFamily="Roboto" buttonId="it-simpleFormSubmit" />
        </div>
      </form>
    );
  }
}

export default function(inputs: FormInput[], initialValues?: DataShape) {
  return reduxForm({
    destroyOnUnmount: false,
    form: 'simpleForm',
    initialValues,
    onSubmitFail: syncErrors => formUtils.focusOnSubmitFail(syncErrors, SIMPLE_FORM_ERROR_ID),
    validate: (values: DataShape) => formUtils.validate(inputs, values) as any,
  })(SimpleForm as any) as typeof SimpleForm;
}
